import { apiClient } from './client';

/**
 * 글꼴 등록 API (2026-08-18).
 *
 * SSOT 는 chat-server(`/api/v1/fonts*`) 다 — 글꼴 **바이트**를 MinIO 에 두는데 그 통로를 가진
 * 쪽이 chat-server 이기 때문이다. 관리 콘솔은 BFF 의 catch-all proxy 를 그대로 타므로
 * admin BFF 에 라우트를 새로 만들 필요가 없다(사용자 상세 편집 PR#306 에서 확인된 규약).
 *
 * ⚠ BFF 는 **JSON 전용**이라 파일을 base64 로 올린다(회사 로고와 같은 이유). 그래서 실제 본문은
 *   파일 크기의 약 1.34배가 된다 — 서버 상한 8MB 에 맞춰 UI 가 미리 걸러 준다.
 */

export interface FontRow {
  id: string;
  family: string;
  weight: number;
  style: 'normal' | 'italic' | string;
  format: string;
  sizeBytes: number;
  createdAt?: string;
}

export interface FontUploadInput {
  family: string;
  weight: number;
  style: 'normal' | 'italic';
  /** 파일 바이트(base64, 접두사 없음). */
  data: string;
  /** 어디서 온 등록인지 (2026-08-20 추가, 선택). 옛 서버는 그냥 무시한다. */
  source?: 'file' | 'zip' | 'url';
  sourceUrl?: string;
}

export async function listFonts(): Promise<FontRow[]> {
  const { data } = await apiClient.get<{ fonts: FontRow[] }>('/fonts');
  return Array.isArray(data?.fonts) ? data.fonts : [];
}

export async function uploadFont(input: FontUploadInput): Promise<FontRow> {
  const { data } = await apiClient.post<{ font: FontRow }>('/fonts', input);
  return data.font;
}

export async function deleteFont(id: string): Promise<void> {
  await apiClient.delete(`/fonts/${id}`);
}

/** 파일 → base64(접두사 제거). 큰 파일도 메인 스레드를 오래 잡지 않게 FileReader 를 쓴다. */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('파일을 읽지 못했습니다'));
    reader.onload = () => {
      const result = String(reader.result ?? '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.readAsDataURL(file);
  });
}

/* ══════════════════════════════════════════════════════════════════════════
   글꼴 관리 확장 (2026-08-20) — 위 기본 3종(목록·등록·해제)은 그대로 두고 덧붙인다.

   ⚠ 이 아래 호출은 **새 chat-server** 가 있어야 한다. 옛 서버에서는 404 가 오므로
     화면이 그걸 알아보고 "서버가 아직 이 기능을 모릅니다" 로 말한다(배포 순서 결합 제거).
   ══════════════════════════════════════════════════════════════════════════ */

/** 등록 출처 — 목록에서 파일/압축/주소를 갈라 보여 준다. */
export type FontSource = 'file' | 'zip' | 'url';

export interface FontRowFull extends FontRow {
  /** 꺼 두면 파일은 남고 `fonts.css` 에서만 빠진다. 옛 서버는 이 값을 안 주므로 true 로 본다. */
  enabled?: boolean;
  source?: FontSource;
  sourceUrl?: string | null;
  createdByName?: string | null;
}

export interface FontAlias {
  id: string;
  family: string;
  alias: string;
}

export interface FontRegistry {
  fonts: FontRowFull[];
  aliases: FontAlias[];
}

/** 목록 + 별칭을 한 번에. 옛 서버는 aliases 를 안 주므로 빈 배열로 받는다. */
export async function listFontRegistry(): Promise<FontRegistry> {
  const { data } = await apiClient.get<{ fonts?: FontRowFull[]; aliases?: FontAlias[] }>('/fonts');
  return {
    fonts: Array.isArray(data?.fonts) ? data.fonts : [],
    aliases: Array.isArray(data?.aliases) ? data.aliases : [],
  };
}

export interface FontUrlInput {
  url: string;
  family?: string;
  weight?: number;
  style?: 'normal' | 'italic';
}

/** CSS 주소를 넣으면 그 안의 글꼴 주소로 **펼쳐서** 돌아온다(등록이 아니라 목록). */
export interface FontUrlExpanded {
  url: string;
  family: string | null;
  weight: number | null;
  style: 'normal' | 'italic' | null;
}

export type FontUrlResult =
  | { kind: 'font'; font: FontRowFull }
  | { kind: 'expanded'; expanded: FontUrlExpanded[] };

/** 확인만 — 서버가 받아 형식·용량을 알려 주고 **저장하지 않는다**. 표를 채우는 데 쓴다. */
export interface FontUrlProbe {
  url: string;
  family: string;
  format: string;
  sizeBytes: number;
}

export type FontUrlCheck =
  | { kind: 'probe'; probe: FontUrlProbe }
  | { kind: 'expanded'; expanded: FontUrlExpanded[] };

export async function probeFontUrl(url: string): Promise<FontUrlCheck> {
  const { data } = await apiClient.post<{ probe?: FontUrlProbe; expanded?: FontUrlExpanded[] }>(
    '/fonts/from-url', { url, check: true },
  );
  if (Array.isArray(data?.expanded)) return { kind: 'expanded', expanded: data.expanded };
  return { kind: 'probe', probe: data.probe as FontUrlProbe };
}

export async function registerFontFromUrl(input: FontUrlInput): Promise<FontUrlResult> {
  const { data } = await apiClient.post<{ font?: FontRowFull; expanded?: FontUrlExpanded[] }>(
    '/fonts/from-url', input,
  );
  if (Array.isArray(data?.expanded)) return { kind: 'expanded', expanded: data.expanded };
  return { kind: 'font', font: data.font as FontRowFull };
}

/** 사용 중 스위치 — 가족 통째로. 되돌릴 수 있는 해제라 삭제보다 먼저 권한다. */
export async function setFontFamilyEnabled(family: string, enabled: boolean): Promise<number> {
  const { data } = await apiClient.patch<{ faces?: number }>('/fonts/family', { family, enabled });
  return Number(data?.faces ?? 0);
}

export async function addFontAlias(family: string, alias: string): Promise<FontAlias> {
  const { data } = await apiClient.post<{ alias: FontAlias }>('/fonts/aliases', { family, alias });
  return data.alias;
}

export async function deleteFontAlias(id: string): Promise<void> {
  await apiClient.delete(`/fonts/aliases/${id}`);
}

/**
 * 등록된 글꼴 파일 주소 — 관리 콘솔이 `@font-face` 의 `src` 로 쓴다.
 *
 * ⚠ 글꼴 로딩에는 Authorization 헤더를 못 붙인다 → **admin_at 쿠키**로 BFF 에 닿는다.
 *   그래서 이 주소는 axios 가 아니라 브라우저가 직접 받는다(로고를 `<img src>` 로 쓰는 자리와 같다).
 */
export function fontFileUrl(id: string): string {
  const base = import.meta.env.VITE_API_BASE_URL ?? '/api';
  return `${base}/fonts/${id}/file`;
}
